import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import ENV from 'donor-elf-web/config/environment';
import dateUtils from 'donor-elf-web/lib/date-utils';
import FundSummaryModel from 'donor-elf-web/models/fund-summary';
import FormatAmountService from 'donor-elf-web/services/format-amount-service';
import Highcharts from 'highcharts';
import moment from 'moment';

interface ComponentArgs {
  fundSummary: FundSummaryModel;
  height?: number;
  showIncome: boolean;
  showExpense: boolean;
  showBalance: boolean;
  showGoal: boolean;
}

export default class FundSummaryChart extends Component<ComponentArgs> {
  @service declare formatAmountService: FormatAmountService;

  get fundSummary() {
    return this.args.fundSummary;
  }

  get height() {
    return this.args.height || 200;
  }

  fontFamily =
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

  get monthlyBalance() {
    return this.fundSummary.monthlyData.map((item: any) => {
      return {
        y: item.balance,
        monthlyData: item,
      };
    });
  }

  get monthlyIncome() {
    return this.fundSummary.monthlyData.map((item: any) => {
      return {
        y: item.income,
        monthlyData: item,
        hasMonthlyGoal: this.fundSummary.hasMonthlyGoal,
        monthlyGoal: this.fundSummary.monthlyGoal,
      };
    });
  }

  get monthlyExpense() {
    return this.fundSummary.monthlyData.map((item: any) => {
      return {
        y: item.expense * -1,
        monthlyData: item,
        hasMonthlyGoal: this.fundSummary.hasMonthlyGoal,
        monthlyGoal: this.fundSummary.monthlyGoal,
      };
    });
  }

  get monthlyGoal() {
    return this.fundSummary.monthlyData.map(() => {
      return {
        y: this.fundSummary.monthlyGoal,
      };
    });
  }

  get categories() {
    return this.fundSummary.monthlyData.map((item: any) => {
      return moment(`${item.year}-${item.month}-02`, 'YYYY-M-DD').format(
        'MMM YYYY',
      );
    });
  }

  generateFromAndToParams(item: any) {
    let monthString = item.month.toString();
    if (monthString.length === 1) {
      monthString = `0${monthString}`;
    }
    let date = dateUtils.deserializeDateOnly(`${item.year}-${monthString}-01`);
    return `from=${moment(date).format('YYYY-MM-DD')}&to=${moment(date).endOf('month').format('YYYY-MM-DD')}`;
  }

  get monthlyGiftsChartSettings() {
    let { formatAmountService } = this;
    let fundId = this.fundSummary.fundId;
    let self = this;

    let series = [];
    if (this.args.showIncome) {
      series.push({
        type: 'column',
        name: 'Monthly Income',
        color: '#0b68a1',
        tooltip: {
          pointFormatter: function () {
            let message = `Income: ${formatAmountService.format(this.y)}`;
            if (this.hasMonthlyGoal) {
              let percent = Math.round((this.y * 100) / this.monthlyGoal);
              message = `${message} (${percent}%)`;
            }
            return message;
          },
        },
        events: {
          click(event: any) {
            window.location.href = `${ENV.rootURL}funds/${fundId}/transactions?incomeOrExpense=income&${self.generateFromAndToParams(event.point.monthlyData)}`;
          },
        },
        data: this.monthlyIncome,
      });
    }
    if (this.args.showExpense) {
      series.push({
        type: 'column',
        name: 'Monthly Expense',
        color: '#d9534f',
        tooltip: {
          pointFormatter: function () {
            let message = `Expense: ${formatAmountService.format(this.y)}`;
            if (this.hasMonthlyGoal) {
              let percent = Math.round((this.y * 100) / this.monthlyGoal);
              message = `${message} (${percent}%)`;
            }
            return message;
          },
        },
        events: {
          click(event: any) {
            window.location.href = `${ENV.rootURL}funds/${fundId}/transactions?incomeOrExpense=expense&${self.generateFromAndToParams(event.point.monthlyData)}`;
          },
        },
        data: this.monthlyExpense,
      });
    }
    if (this.args.showGoal) {
      series.push({
        type: 'line',
        name: 'Monthly Goal',
        color: '#f0ad4e',
        tooltip: {
          pointFormatter: function () {
            return `Goal: ${formatAmountService.format(this.y)}`;
          },
        },
        lineWidth: 3,
        marker: {
          enabled: false,
        },
        data: this.monthlyGoal,
      });
    }
    if (this.args.showBalance) {
      series.push({
        type: 'line',
        name: 'Monthly Balance',
        color: '#5cb85c',
        tooltip: {
          pointFormatter: function () {
            return `Balance: ${formatAmountService.format(this.y)}`;
          },
        },
        events: {
          click() {
            window.location.href = `${ENV.rootURL}funds/${fundId}/monthly-summary`;
          },
        },
        lineWidth: 3,
        marker: {
          enabled: false,
        },
        data: this.monthlyBalance,
      });
    }

    return {
      chart: {
        marginBottom: 7,
        marginTop: 0,
        height: this.height,
      },
      title: {
        text: '',
      },
      credits: {
        enabled: false,
      },
      tooltip: {
        enabled: true,
        headerFormat: '<span style="font-size: 14px">{point.key}</span><br/>',
        style: {
          fontSize: '16px',
        },
      },
      plotOptions: {
        column: {
          groupPadding: 0.2,
          pointPadding: 0.01,
        },
      },
      xAxis: {
        labels: {
          enabled: false,
        },
        visible: false,
        categories: this.categories,
      },
      yAxis: {
        labels: {
          enabled: false,
        },
        title: {
          text: null,
        },
        startOnTick: false,
        endOnTick: false,
        tickPositions: [0],
      },
      legend: {
        enabled: false,
      },
      series,
    };
  }

  @action
  renderChart(e: any) {
    Highcharts.chart(e, this.monthlyGiftsChartSettings);
  }
}
